import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from "../../services/api";
import { API_ENDPOINTS } from "../../config/api";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addYears, addMonths } from 'date-fns';

export default function ClientRegistration() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    client_name: '',
    name: '',
    email: '',
    password: '',
    passwordConfirmation: '',
    expiresAt: addYears(new Date(), 1), // Default to 1 year from now
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [openDatePicker, setOpenDatePicker] = useState(false);

  const handleSubmit = async (event, createAnother = false) => {
    event.preventDefault();
    
    if (formData.password !== formData.passwordConfirmation) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const { passwordConfirmation, ...requestData } = formData;
      // Format the date to ISO string before sending
      requestData.expiresAt = formData.expiresAt.toISOString();
      const response = await api.post(API_ENDPOINTS.AUTH.REGISTER, requestData);
      
      if (response.status === 202 || response.status === 201 || response.status === 200) {
        setOpenSnackbar(true);
        if (!createAnother) {
          navigate('/client-management');
        } else {
          setFormData({
            client_name: '',
            name: '',
            email: '',
            password: '',
            passwordConfirmation: '',
            expiresAt: addYears(new Date(), 1), // Reset to default
          });
        }
      } else {
        setError(response.data.message[0] || 'Failed to register client. Please try again.');
      }
    } catch (err) {
      console.error('Error registering client:', err);
      setError(err.response?.data?.message[0] || 'Failed to register client. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowPasswordConfirmation = () => {
    setShowPasswordConfirmation(!showPasswordConfirmation);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const handleDurationClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDurationClose = () => {
    setAnchorEl(null);
  };

  const handleDurationSelect = (duration) => {
    let newDate;
    switch (duration) {
      case '1month':
        newDate = addMonths(new Date(), 1);
        break;
      case '3months':
        newDate = addMonths(new Date(), 3);
        break;
      case '6months':
        newDate = addMonths(new Date(), 6);
        break;
      case '1year':
        newDate = addYears(new Date(), 1);
        break;
      case '2years':
        newDate = addYears(new Date(), 2);
        break;
      default:
        newDate = addYears(new Date(), 1);
    }
    setFormData(prev => ({
      ...prev,
      expiresAt: newDate
    }));
    handleDurationClose();
  };

  return (
    <Box sx={{ width: '100%', pl: 1, pr: 2, backgroundColor: 'background.paper', borderRadius: 8 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Register New Client
        </Typography>
      </Box>
      <Paper 
        sx={{ 
          p: 2, 
          borderRadius: 6,
          boxShadow: '0 2px 5px rgba(0,0,0,0.5)',
          backgroundColor: 'background.paper'
        }}
      >
        <Box component="form" onSubmit={(e) => handleSubmit(e, false)}>
          {error && (
            <Typography color="error" sx={{ mb: 4 }}>
              {error}
            </Typography>
          )}
          
          <Box sx={{ mb: 3 }}>
            <TextField
              required
              fullWidth
              label="Client Name"
              name="client_name"
              value={formData.client_name}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 20,
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#C41747',
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              required
              fullWidth
              label="Admin Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 20,
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#C41747',
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              required
              fullWidth
              type="email"
              label="Admin Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 20,
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#C41747',
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 3, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              required
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="Password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      sx={{ color: 'text.primary' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 20,
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#C41747',
                  },
                },
              }}
            />
            <TextField
              required
              fullWidth
              type={showPasswordConfirmation ? 'text' : 'password'}
              label="Password confirmation"
              name="passwordConfirmation"
              value={formData.passwordConfirmation}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClickShowPasswordConfirmation}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      sx={{ color: 'text.primary' }}
                    >
                      {showPasswordConfirmation ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 20,
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#C41747',
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                required
                label="Subscription Expiry Date"
                value={formData.expiresAt}
                onChange={(newValue) => {
                  setFormData(prev => ({
                    ...prev,
                    expiresAt: newValue
                  }));
                }}
                format="dd MMMM yyyy"
                open={openDatePicker}
                onOpen={() => setOpenDatePicker(true)}
                onClose={() => setOpenDatePicker(false)}
                onClick={() => setOpenDatePicker(true)}
                slotProps={{
                  textField: {
                    onClick: () => setOpenDatePicker(true)
                  }
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 20,
                    '& fieldset': {
                      borderColor: '#E0E0E0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#C41747',
                    },
                  },
                }}
              />
            </LocalizationProvider>
            <Button
              onClick={handleDurationClick}
              variant="outlined"
              sx={{
                color: '#E31B54',
                borderColor: '#E31B54',
                textTransform: 'none',
                borderRadius: 20,
                '&:hover': {
                  borderColor: '#C41747',
                  bgcolor: 'rgba(227, 27, 84, 0.04)',
                },
              }}
            >
              Preset Durations
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleDurationClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                elevation: 3,
                sx: {
                  mt: 1,
                  minWidth: 180,
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  '& .MuiMenuItem-root': {
                    px: 2,
                    py: 1.5,
                    borderRadius: 1,
                    margin: '4px 8px',
                    typography: 'body2',
                    '&:hover': {
                      backgroundColor: 'rgba(227, 27, 84, 0.08)',
                      color: '#E31B54',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(227, 27, 84, 0.12)',
                      color: '#E31B54',
                      '&:hover': {
                        backgroundColor: 'rgba(227, 27, 84, 0.16)',
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem onClick={() => handleDurationSelect('1month')} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography>1 Month</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleDurationSelect('3months')} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography>3 Months</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleDurationSelect('6months')} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography>6 Months</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleDurationSelect('1year')} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography>1 Year</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleDurationSelect('2years')} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography>2 Years</Typography>
              </MenuItem>
            </Menu>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: '#E31B54',
                color: '#FFFFFF',
                textTransform: 'none',
                borderRadius: 20,
                px: 4,
                '&:hover': { bgcolor: '#C41747' },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Register'}
            </Button>
            <Button
              variant="outlined"
              onClick={(e) => handleSubmit(e, true)}
              disabled={loading}
              sx={{
                color: '#E31B54',
                borderColor: '#E31B54',
                textTransform: 'none',
                borderRadius: 20,
                px: 4,
                '&:hover': {
                  borderColor: '#C41747',
                  bgcolor: 'rgba(227, 27, 84, 0.04)',
                },
              }}
            >
              Register & create another
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/users')}
              sx={{
                color: "#F9826E",
                borderColor: "#F9826E",
                textTransform: "none",
                borderRadius: 20,
                "&:hover": {
                  color: "black",
                  borderColor: "#F7583D",
                  bgcolor: "#F9826E",
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Client registered successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
} 